/* Native scrolling keeps touch swipes smooth without an animation dependency. */
if (!customElements.get('solvia-review-slider')) {
  customElements.define('solvia-review-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('[data-review-track]');
      this.cards = [...this.querySelectorAll('.solvia-review')];
      if (!this.track || !this.cards.length) return;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.previous = this.querySelector('[data-review-previous]');
      this.next = this.querySelector('[data-review-next]');
      this.position = this.querySelector('[data-review-position]');
      this.dialog = this.querySelector('[data-review-lightbox]');
      this.fullPhoto = this.querySelector('[data-review-full-photo]');
      this.fullCopy = this.querySelector('[data-review-full-copy]');
      this.photoPrevious = this.querySelector('[data-photo-previous]');
      this.photoNext = this.querySelector('[data-photo-next]');
      this.photoPosition = this.querySelector('[data-photo-position]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.querySelector('[data-review-controls]').hidden = false;
      this.dataset.ready = 'true';

      this.previous.addEventListener('click', () => this.advance(-1), { signal });
      this.next.addEventListener('click', () => this.advance(1), { signal });
      this.track.addEventListener('scroll', () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.sync(false);
        });
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => {
          this.destination = null;
          this.sync(true);
        }, 150);
      }, { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') this.moveTo(0);
        else if (event.key === 'End') this.moveTo(this.cards.length - 1);
        else this.advance(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });

      this.track.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('a, button')) return;
        this.drag = { x: event.clientX, left: this.track.scrollLeft, id: event.pointerId, active: false };
        this.destination = null;
      }, { signal });
      this.track.addEventListener('pointermove', event => {
        if (!this.drag) return;
        const distance = event.clientX - this.drag.x;
        if (!this.drag.active && Math.abs(distance) < 6) return;
        if (!this.drag.active) {
          this.drag.active = true;
          this.track.setPointerCapture(event.pointerId);
          this.track.classList.add('is-dragging');
        }
        event.preventDefault();
        this.track.scrollLeft = this.drag.left - distance;
      }, { signal });
      const endDrag = () => {
        if (!this.drag) return;
        const { active, id } = this.drag;
        this.drag = null;
        if (this.track.hasPointerCapture(id)) this.track.releasePointerCapture(id);
        this.track.classList.remove('is-dragging');
        if (active) this.moveTo(this.nearestIndex());
      };
      this.track.addEventListener('pointerup', endDrag, { signal });
      this.track.addEventListener('pointercancel', endDrag, { signal });
      this.track.addEventListener('pointerleave', () => {
        if (this.drag && !this.drag.active) this.drag = null;
      }, { signal });

      this.querySelectorAll('[data-review-photo]').forEach(link => {
        link.addEventListener('click', event => {
          if (!this.dialog?.showModal || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.openReview(link.closest('.solvia-review'), link);
        }, { signal });
      });
      this.querySelectorAll('[data-review-open]').forEach(button => {
        button.addEventListener('click', () => this.openReview(button.closest('.solvia-review')), { signal });
      });
      this.photoPrevious?.addEventListener('click', () => this.showPhoto(this.photoIndex - 1), { signal });
      this.photoNext?.addEventListener('click', () => this.showPhoto(this.photoIndex + 1), { signal });
      this.dialog?.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.showPhoto(this.photoIndex + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal });
      this.querySelector('[data-review-close]')?.addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog?.addEventListener('click', event => {
        if (event.target === this.dialog) this.dialog.close();
      }, { signal });

      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('.solvia-review');
        const index = this.cards.indexOf(card);
        if (index >= 0) this.moveTo(index);
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.sync(true));
      this.resizeObserver.observe(this.track);
      this.sync(true);
    }

    openReview(card, photo) {
      if (!this.dialog?.showModal) return;
      this.activePhotos = [...card.querySelectorAll('[data-review-photo]')];
      this.fullCopy.replaceChildren(card.querySelector('[data-review-details]').content.cloneNode(true));
      this.showPhoto(Math.max(0, this.activePhotos.indexOf(photo)));
      this.querySelector('[data-review-photo-controls]').hidden = this.activePhotos.length < 2;
      this.dialog.showModal();
      this.dialog.scrollTop = 0;
      this.fullCopy.scrollTop = 0;
    }

    showPhoto(index) {
      this.photoIndex = Math.max(0, Math.min(this.activePhotos.length - 1, index));
      const link = this.activePhotos[this.photoIndex];
      this.fullPhoto.src = link.href;
      this.fullPhoto.alt = link.querySelector('img').alt;
      this.fullPhoto.width = link.querySelector('img').width;
      this.fullPhoto.height = link.querySelector('img').height;
      this.photoPrevious.disabled = this.photoIndex === 0;
      this.photoNext.disabled = this.photoIndex === this.activePhotos.length - 1;
      this.photoPosition.textContent = `${this.photoIndex + 1} / ${this.activePhotos.length}`;
    }

    nearestIndex() {
      const step = this.cards[1] ? this.cards[1].offsetLeft - this.cards[0].offsetLeft : this.cards[0].offsetWidth;
      return Math.max(0, Math.min(this.cards.length - 1, Math.round(this.track.scrollLeft / step)));
    }

    advance(direction) {
      this.moveTo((this.destination ?? this.nearestIndex()) + direction);
    }

    moveTo(index) {
      this.destination = Math.max(0, Math.min(this.cards.length - 1, index));
      const left = this.cards[this.destination].offsetLeft - this.cards[0].offsetLeft;
      this.track.scrollTo({ left: Math.min(left, this.track.scrollWidth - this.track.clientWidth), behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    }

    sync(announce) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.previous.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max - 2;
      if (!announce) return;
      const viewport = this.track.getBoundingClientRect();
      const visible = this.cards.map((card, index) => {
        const rect = card.getBoundingClientRect();
        const overlap = Math.min(rect.right, viewport.right) - Math.max(rect.left, viewport.left);
        return overlap > Math.min(rect.width, viewport.width) * .6 ? index + 1 : null;
      }).filter(Boolean);
      if (!visible.length) return;
      const range = visible.length === 1 ? visible[0] : `${visible[0]}–${visible[visible.length - 1]}`;
      this.position.textContent = `${range} of ${this.cards.length}`;
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.resizeObserver?.disconnect();
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.frame);
      if (this.dialog?.open) this.dialog.close();
    }
  });
}
